import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Flex,
  Button,
  Modal,
  ModalOverlay,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalFooter,
  Heading,
  Alert,
  AlertDescription,
  Checkbox,
  Box,
} from '@chakra-ui/react';
import deepEqual from 'fast-deep-equal/es6/react';
import { SearchV2, SkeletonLoader } from 'shared-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from '@/store';
import { IStudentRow, IProgrammableTag, ITag } from 'types';
import { bulkAction, requestSelectedTags } from '@/store/dashboard/actions';
import { getAllTags, sortWIthIntakeFirst } from '@/utilities/tags';
import useDebouncedCallback from '@/utilities/customHooks/useDebouncedCallback';
import { differenceWith } from 'lodash';
import { programmableTagsActions } from '@/store/programmable_tags/reducer';
import { BulkActionTypeEnum } from '@/store/studentUsers/types';
import { useTranslation } from "react-i18next";

const serializeTags = (
  selectedStudents: IStudentRow[],
  tags: Array<ITag | IProgrammableTag>,
  isPathway: boolean,
  allSelected = false,
  tabTotalsCount?: number,
  selectedTagsTotals?: { [key: string]: number },
) => {
  return tags.map(tag => {
    let countOfStudentsWithTag = 0;

    selectedStudents.forEach(student => {
      const studentTags = getAllTags({ tags: student.tags, isPathway });
      const tagsSortedWithIntake = sortWIthIntakeFirst(studentTags);
      if (tagsSortedWithIntake.some(studentTag => studentTag.id === tag.id)) {
        countOfStudentsWithTag++;
      }
    });

    const allSelectedStudentsHaveTag = countOfStudentsWithTag === selectedStudents.length;
    const atLeastOneSelectedStudentHasTag = countOfStudentsWithTag > 0;
    const selectedTagsCount = selectedTagsTotals ? selectedTagsTotals[tag.id] : undefined;
    const checked = allSelected ? tabTotalsCount === selectedTagsCount : allSelectedStudentsHaveTag;
    const indeterminate = allSelected
      ? selectedTagsCount && tabTotalsCount !== selectedTagsCount
      : atLeastOneSelectedStudentHasTag;

    return {
      ...tag,
      checked,
      indeterminate,
    };
  });
};

const serializeTagChange = (event: React.ChangeEvent<HTMLInputElement>, tags: Array<ITag | IProgrammableTag>) => {
  return tags.map(tag =>
    tag.id === event.target.value
      ? {
          ...tag,
          checked: event.target.checked,
          indeterminate: false,
        }
      : { ...tag },
  );
};
export const EditTags = () => {
    const { t } = useTranslation('common');
  const isPathway = useSelector(({ config }: ApplicationState) => (config?.pathwayIds?.length ?? 0) > 0);
  const customTags = useSelector((state: ApplicationState) => state.config.tags || [], deepEqual);
  const programmableTags = useSelector(
    ({ programmableTags }: ApplicationState) => programmableTags.programmableTags,
    deepEqual,
  );
  const students: IStudentRow[] = useSelector(({ dashboard }: ApplicationState) => dashboard.students || [], deepEqual);
  const selectedRows = useSelector(({ dashboard }: ApplicationState) => dashboard.selectedRows, deepEqual);
  const allSelected = useSelector(({ dashboard }: ApplicationState) => dashboard.allSelected, deepEqual);
  const tabs = useSelector(({ dashboard }: ApplicationState) => dashboard.tabs, deepEqual);
  const selectedTagsLoaded = useSelector(({ dashboard }: ApplicationState) => dashboard.selectedTagsLoaded, deepEqual);
  const selectedTagsTotals = useSelector(({ dashboard }: ApplicationState) => dashboard.selectedTagsTotals, deepEqual);
  const tabTotalsCount = tabs?.find(({ active }) => active)?.count || 0;
  const dispatch = useDispatch();
  const selectedStudents = useMemo(
    () => students.filter(student => selectedRows.includes(student.p_key)),
    [selectedRows, students],
  );
  const serializedCustomTags = useMemo(
    () => serializeTags(selectedStudents, customTags, isPathway, allSelected, tabTotalsCount, selectedTagsTotals),
    [customTags, isPathway, selectedStudents, allSelected, tabTotalsCount, selectedTagsTotals],
  );
  const serializedProgrammableTags = useMemo(
    () => serializeTags(selectedStudents, programmableTags, isPathway, allSelected, tabTotalsCount, selectedTagsTotals),
    [isPathway, programmableTags, selectedStudents, allSelected, tabTotalsCount, selectedTagsTotals],
  );
  const [isOpenedEditTagsModal, setIsOpenedEditTagsModal] = useState(false);
  const [customTagsSearched, setCustomTagsSearched] = useState(serializedCustomTags);
  const [programmableTagsSearched, setProgrammableTagsSearched] = useState(serializedProgrammableTags);
  useEffect(() => {
    dispatch(programmableTagsActions.getProgrammableTagRecords());
  }, [dispatch]);
  useEffect(() => {
    setProgrammableTagsSearched(serializedProgrammableTags);
  }, [serializedProgrammableTags]);
  useEffect(() => {
    setCustomTagsSearched(serializedCustomTags);
  }, [serializedCustomTags]);
  const handleCustomTagsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomTagsSearched(prevState => serializeTagChange(event, prevState) as typeof customTagsSearched);
  };
  const handleProgrammableTagsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProgrammableTagsSearched(prevState => serializeTagChange(event, prevState) as typeof programmableTagsSearched);
  };
  const handleOpenEditTagsModal = () => {
    setIsOpenedEditTagsModal(true);
  };
  const handleCloseEditTagsModal = () => {
    setIsOpenedEditTagsModal(false);
  };
  const handleClearAllCustomTags = () => {
    setCustomTagsSearched(prevState => prevState.map(tag => ({ ...tag, checked: false, indeterminate: false })));
  };
  const handleClearAllProgrammableTags = () => {
    setProgrammableTagsSearched(prevState => prevState.map(tag => ({ ...tag, checked: false, indeterminate: false })));
  };
  const handleSearch = useDebouncedCallback((value: string) => {
    if (value) {
      setCustomTagsSearched(
        serializedCustomTags.filter(item => item.label.toLowerCase().trim().includes(value.toLowerCase().trim())),
      );
      setProgrammableTagsSearched(
        serializedProgrammableTags.filter(item => item.label.toLowerCase().trim().includes(value.toLowerCase().trim())),
      );
    } else {
      setProgrammableTagsSearched(serializedProgrammableTags);
      setCustomTagsSearched(serializedCustomTags);
    }
  }, 500);
  const handleConfirmEditTagsModal = useCallback(() => {
    const customTagsChanged = differenceWith(
      customTagsSearched,
      serializedCustomTags,
      (o1, o2) => o1.id === o2.id && o1.checked === o2.checked && o1.indeterminate === o2.indeterminate,
    );
    const programmableTagsChanged = differenceWith(
      programmableTagsSearched,
      serializedProgrammableTags,
      (o1, o2) => o1.id === o2.id && o1.checked === o2.checked && o1.indeterminate === o2.indeterminate,
    );
    const programmableTagsToUnpin = programmableTagsChanged
      .filter(tag => !tag.checked && !tag.indeterminate)
      .map(tag => ({ id: tag.id, isProgrammable: true }));
    const programmableTagsToPin = programmableTagsChanged
      .filter(tag => tag.checked && !tag.indeterminate)
      .map(tag => ({ id: tag.id, isProgrammable: true }));
    const customTagToUnpin = customTagsChanged
      .filter(tag => !tag.checked && !tag.indeterminate)
      .map(tag => ({ id: tag.id, isProgrammable: false }));
    const customTagsToPin = customTagsChanged
      .filter(tag => tag.checked && !tag.indeterminate)
      .map(tag => ({ id: tag.id, isProgrammable: false }));
    const tagsToPin = [...customTagsToPin, ...programmableTagsToPin];
    const tagsToUnpin = [...customTagToUnpin, ...programmableTagsToUnpin];
    if (tagsToUnpin.length) {
      dispatch(
        bulkAction(
          BulkActionTypeEnum.REMOVE_TAGS,
          tagsToUnpin.map(tag => tag.id),
        ),
      );
    }
    if (tagsToPin.length) {
      dispatch(
        bulkAction(
          BulkActionTypeEnum.ADD_TAGS,
          tagsToPin.map(tag => tag.id),
        ),
      );
    }
    setIsOpenedEditTagsModal(false);
  }, [customTagsSearched, dispatch, programmableTagsSearched, serializedCustomTags, serializedProgrammableTags]);
  const handleRemoveAllEditTagsModal = () => {
    const customTagsPinned = differenceWith(
      serializedCustomTags.map(item => ({ ...item, checked: false, indeterminate: false })),
      serializedCustomTags,
      (o1, o2) => o1.id === o2.id && o1.checked === o2.checked && o1.indeterminate === o2.indeterminate,
    ).map(tag => ({ id: tag.id, isProgrammable: false }));
    const programmableTagsPinned = differenceWith(
      serializedProgrammableTags.map(item => ({ ...item, checked: false, indeterminate: false })),
      serializedProgrammableTags,
      (o1, o2) => o1.id === o2.id && o1.checked === o2.checked && o1.indeterminate === o2.indeterminate,
    ).map(tag => ({ id: tag.id, isProgrammable: true }));
    const tagsToUnpin = [...customTagsPinned, ...programmableTagsPinned];
    if (tagsToUnpin.length) {
      dispatch(
        bulkAction(
          BulkActionTypeEnum.REMOVE_TAGS,
          tagsToUnpin.map(tag => tag.id),
        ),
      );
    }
    setIsOpenedEditTagsModal(false);
  };

  useEffect(() => {
    if (isOpenedEditTagsModal && allSelected) {
      dispatch(requestSelectedTags());
    }
  }, [dispatch, isOpenedEditTagsModal, allSelected]);
  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        id="edit-tags"
        onClick={handleOpenEditTagsModal}
        leftIcon={<FontAwesomeIcon icon={regular('tags')} className="utility-icon" />}
        data-selector="edit-tags-button"
      >
        {t('common:editTags')}
                    </Button>
      <Modal onClose={handleCloseEditTagsModal} isOpen={isOpenedEditTagsModal} size="md" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader display="flex">
            {t('common:editTags')}
                                  <SearchV2 onSearch={handleSearch} />
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedTagsLoaded ? (
              <Box mt={4} mb={12}>
                <SkeletonLoader />
              </Box>
            ) : (
              <>
                {customTagsSearched.length < 1 && programmableTagsSearched.length < 1 && (
                  <Alert status="warning" fontSize="sm">
                    <AlertDescription>{t('common:noTagsAvailable')}</AlertDescription>
                  </Alert>
                )}
                {customTagsSearched.length > 0 && (
                  <Flex mb="1" alignItems="center" justifyContent="space-between">
                    <Heading as="h3" fontSize="md">
                      {t('common:userTags')}
                                                                  </Heading>
                    <Button size="sm" variant="ghost" onClick={handleClearAllCustomTags}>
                      {t('common:remove')}
                                                                  </Button>
                  </Flex>
                )}
                {customTagsSearched.map(({ id, label, checked, indeterminate }) => (
                  <Checkbox
                    key={id}
                    id={id}
                    value={id}
                    onChange={handleCustomTagsChange}
                    isChecked={checked}
                    isIndeterminate={!checked && indeterminate}
                    size="md"
                    variant="ghost"
                    mb="0.5"
                    data-selector={id}
                  >
                    {label}
                  </Checkbox>
                ))}
                {programmableTagsSearched.length > 0 && (
                  <Flex mb="1" alignItems="center" justifyContent="space-between">
                    <Heading as="h3" fontSize="md">
                      {t('common:programmableTags')}
                                                                  </Heading>
                    <Button size="sm" variant="ghost" onClick={handleClearAllProgrammableTags}>
                      {t('common:remove')}
                                                                  </Button>
                  </Flex>
                )}
                {programmableTagsSearched.map(({ id, label, checked, indeterminate }) => (
                  <Checkbox
                    key={id}
                    id={id}
                    value={id}
                    onChange={handleProgrammableTagsChange}
                    isChecked={checked}
                    isIndeterminate={!checked && indeterminate}
                    size="md"
                    variant="ghost"
                    mb="0.5"
                    data-selector={id}
                  >
                    {label}
                  </Checkbox>
                ))}
              </>
            )}
          </ModalBody>
          {!selectedTagsLoaded && (
            <ModalFooter gap="3">
              <Button size="md" mr="auto" onClick={handleRemoveAllEditTagsModal}>
                {t('common:removeAll')}
                                            </Button>
              <Button size="md" onClick={handleCloseEditTagsModal}>
                {t('common:cancel')}
                                            </Button>
              <Button size="md" variant="solid" onClick={handleConfirmEditTagsModal}>
                {t('common:apply')}
                                            </Button>
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
